/**
 * The Dev-only QA panel (Launch Board L-36; ADR-77; plans/PKG-A.md A4).
 *
 * A "QA" button in the top bar — rendered by the shell only when /healthz says
 * test — opens the shared `Drawer` with the tester's checklist, grouped by
 * screen. Each item takes one answer per tester: Done, Problem, Question,
 * Confusing or Blocked (optionally with what they are waiting on), a note, and
 * the page they were on, captured at the moment they save. Nothing is fetched
 * until the drawer opens.
 */

import {
  QA_STATUSES,
  QA_STATUS_LABEL,
  type QaChecklistItem,
  type QaFeedback,
  type QaStatus,
} from '@propos/shared';
import { ClipboardCheck } from 'lucide-react';
import { useState, type ReactNode } from 'react';

import { ApiError } from '../../app/api.js';
import {
  Button,
  Drawer,
  EmptyState,
  ErrorState,
  TextAreaField,
  TextField,
  useToast,
} from '../../components/ui/index.js';

import { useMyQaFeedback, useQaChecklist, useSaveQaFeedback } from './api.js';

export function QaButton(): ReactNode {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const checklist = useQaChecklist(open);
  const mine = useMyQaFeedback(open);

  const answers = new Map<string, QaFeedback>();
  for (const row of mine.data?.rows ?? []) {
    if (row.itemId !== null) answers.set(row.itemId, row);
  }
  const items = checklist.data?.items ?? [];
  const groups = new Map<string, QaChecklistItem[]>();
  for (const item of items) {
    const key = item.screen ?? item.groupLabel ?? 'General';
    groups.set(key, [...(groups.get(key) ?? []), item]);
  }

  const trigger = (
    <button
      type="button"
      aria-label="QA checklist"
      className="flex min-h-11 items-center gap-1 rounded-ctl px-2 text-ink2 hover:bg-surface-hover hover:text-ink"
    >
      <ClipboardCheck size={19} aria-hidden />
      <span className="text-chip font-semibold">QA</span>
    </button>
  );

  return (
    <Drawer
      trigger={trigger}
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setEditing(null);
      }}
      title="QA checklist"
      closeLabel="Close QA checklist"
    >
      {checklist.isError || mine.isError ? (
        <ErrorState
          body="The QA checklist could not be loaded."
          onRetry={() => {
            void checklist.refetch();
            void mine.refetch();
          }}
        />
      ) : checklist.isPending || mine.isPending ? (
        <p className="py-8 text-center text-body text-ink2">Loading…</p>
      ) : items.length === 0 ? (
        <EmptyState
          title="No checklist yet"
          body="The test checklist has not been loaded. Ask Matt to load it."
        />
      ) : (
        <div className="flex flex-col gap-4 py-2">
          <p className="text-body text-ink2">
            Mark each step as you test it. Saving records the page you are on.
          </p>
          {[...groups.entries()].map(([screen, group]) => (
            <section key={screen} aria-label={screen}>
              <h3 className="mb-1 text-label font-semibold text-ink">{screen}</h3>
              <ul className="flex list-none flex-col divide-y divide-hairline p-0">
                {group.map((item) => (
                  <QaItem
                    key={item.id}
                    item={item}
                    answer={answers.get(item.id)}
                    editing={editing === item.id}
                    onEdit={(on) => {
                      setEditing(on ? item.id : null);
                    }}
                  />
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </Drawer>
  );
}

function QaItem({
  item,
  answer,
  editing,
  onEdit,
}: {
  readonly item: QaChecklistItem;
  readonly answer: QaFeedback | undefined;
  readonly editing: boolean;
  readonly onEdit: (on: boolean) => void;
}): ReactNode {
  const title = item.stepText ?? item.groupLabel ?? item.scriptId;
  return (
    <li className="flex flex-col gap-2 py-2">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="break-words text-body text-ink">{title}</p>
          {item.expectedResult !== null ? (
            <p className="text-chip text-ink2">Expected: {item.expectedResult}</p>
          ) : null}
          {item.roleHint !== null ? (
            <p className="text-chip text-ink2">Role: {item.roleHint}</p>
          ) : null}
          {answer !== undefined ? (
            <p className="text-chip font-semibold text-ink">
              Your answer: {QA_STATUS_LABEL[answer.status]}
              {answer.waitingOn !== null ? ` — waiting on ${answer.waitingOn}` : ''}
            </p>
          ) : null}
        </div>
        <Button
          variant="secondary"
          aria-expanded={editing}
          onClick={() => {
            onEdit(!editing);
          }}
        >
          {answer === undefined ? 'Answer' : 'Change'}
        </Button>
      </div>
      {editing ? (
        <QaAnswerForm
          item={item}
          answer={answer}
          onDone={() => {
            onEdit(false);
          }}
        />
      ) : null}
    </li>
  );
}

function QaAnswerForm({
  item,
  answer,
  onDone,
}: {
  readonly item: QaChecklistItem;
  readonly answer: QaFeedback | undefined;
  readonly onDone: () => void;
}): ReactNode {
  const toast = useToast();
  const save = useSaveQaFeedback();
  const [status, setStatus] = useState<QaStatus | null>(answer?.status ?? null);
  const [note, setNote] = useState(answer?.note ?? '');
  const [waitingOn, setWaitingOn] = useState(answer?.waitingOn ?? '');
  const blocked = status === 'blocked';
  // docs/04 §7: a disabled button says why. What a Blocked tester is waiting
  // on is optional; it is only ever allowed with Blocked (PR #21 review).
  const notReady = status === null ? 'Choose a status first' : undefined;

  const submit = (): void => {
    if (status === null) return;
    // The page the tester is on NOW, not when the drawer opened.
    const fields = {
      status,
      waitingOn: blocked && waitingOn.trim() !== '' ? waitingOn.trim() : null,
      note: note.trim() === '' ? null : note.trim(),
      screenUrl: window.location.href,
    };
    save.mutate(
      answer === undefined
        ? { create: { itemId: item.id, ...fields } }
        : { id: answer.id, write: { version: answer.version, ...fields } },
      {
        onSuccess: () => {
          toast({ message: 'Feedback saved' });
          onDone();
        },
      },
    );
  };

  return (
    <form
      className="flex flex-col gap-3 rounded-ctl border border-hairline p-3"
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      <div role="group" aria-label="Status" className="flex flex-wrap gap-1">
        {QA_STATUSES.map((value) => (
          <Button
            key={value}
            type="button"
            variant={status === value ? 'primary' : 'secondary'}
            aria-pressed={status === value}
            onClick={() => {
              setStatus(value);
            }}
          >
            {QA_STATUS_LABEL[value]}
          </Button>
        ))}
      </div>
      {blocked ? (
        <TextField
          label="Waiting on"
          maxLength={500}
          value={waitingOn}
          onChange={(event) => {
            setWaitingOn(event.target.value);
          }}
        />
      ) : null}
      <TextAreaField
        label="Note"
        maxLength={4000}
        value={note}
        onChange={(event) => {
          setNote(event.target.value);
        }}
      />
      {save.isError ? (
        <p role="alert" className="text-body text-red-fg">
          {save.error instanceof ApiError
            ? save.error.message
            : 'Your feedback could not be saved.'}
        </p>
      ) : null}
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" onClick={onDone}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" disabledReason={notReady} busy={save.isPending}>
          Save
        </Button>
      </div>
    </form>
  );
}
